import { FavoriteType, ICorporationResponse } from '@kcftech/prometheus-client';
import { useUser } from 'contexts/UserContext';
import {
    infiniteCorporationsDataSourceKey,
    useInfiniteCorporationDataSource,
} from 'custom_hooks/data_hooks/data_source/corporationInfiniteDataSource';
import { useDrainDataSource } from 'custom_hooks/data_hooks/data_source/useDrainDataSource';
import { useEffect, useMemo } from 'react';
import { type EntitySelection, MissionControlOrgs } from './mission_control_entity_store';

export type FavoriteEntityInfo = EntitySelection &
    Readonly<{
        /** Corp IDs that are directly favorited or contain a favorited location. */
        relevantCorpIds: Set<string>;
    }>;

export function useFavoriteEntityInfo(): FavoriteEntityInfo {
    const { favoriteIdsByType, favoritesByType } = useUser();

    return useMemo(() => {
        const corpIdSet = favoriteIdsByType.get(FavoriteType.Corporation);
        const locIdSet = favoriteIdsByType.get(FavoriteType.Location);

        const locFavoriteCorpIds = new Set<string>(
            (favoritesByType.get(FavoriteType.Location) ?? []).map(
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                (l) => l.Corporation!.CorporationId,
            ),
        );

        return {
            corporationIds: corpIdSet ?? new Set<string>(),
            locationIds: locIdSet ?? new Set<string>(),
            relevantCorpIds: locFavoriteCorpIds,
        };
    }, [favoriteIdsByType, favoritesByType]);
}

export function useInitializeStore(
    favorites: FavoriteEntityInfo,
    corporations: ICorporationResponse[] | undefined,
) {
    const setCorporations = MissionControlOrgs.useSetCorporations();
    const initialize = MissionControlOrgs.useInitialize();

    // Corporations load page by page, so keep the hierarchy in sync with
    // every page rather than only the first.
    useEffect(() => {
        if (corporations) {
            setCorporations(corporations);
        }
    }, [corporations, setCorporations]);

    useEffect(() => {
        if (corporations) {
            initialize(favorites);
        }
    }, [corporations, favorites, initialize]);
}

export function useCorporationsWithFavorites(favorites: FavoriteEntityInfo) {
    const { results: corporations } = useDrainDataSource(
        useInfiniteCorporationDataSource({
            name: `${infiniteCorporationsDataSourceKey}-mc-entity-nav`,
            defaultFilter: {},
        }),
        { mode: 'page' },
    );

    const { favoriteCorps, otherCorps } = useMemo(() => {
        if (!corporations) {
            return { favoriteCorps: [], otherCorps: [] };
        }
        const favs: ICorporationResponse[] = [];
        const rest: ICorporationResponse[] = [];
        for (const corp of corporations) {
            if (favorites.relevantCorpIds.has(corp.Id)) {
                favs.push(corp);
            } else {
                rest.push(corp);
            }
        }
        return { favoriteCorps: favs, otherCorps: rest };
    }, [corporations, favorites.relevantCorpIds]);

    return { corporations, favoriteCorps, otherCorps };
}
